import { Box, Button, Flex, Icon, Link } from '@chakra-ui/react';
import { FaChevronLeft, FaChevronRight } from '@tryprospect/ui/icons';
import { useMemo } from 'react';

type PaginationItem = number | 'ellipsis-left' | 'ellipsis-right';

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const getPaginationItems = (
  page: number,
  siblingCount: number,
  totalPages?: number,
): PaginationItem[] => {
  const safeSiblingCount = Math.max(0, Math.floor(siblingCount));
  const windowSize = safeSiblingCount * 2 + 1; // pages around current (inclusive)
  const currentPage = totalPages ? clamp(page, 1, totalPages) : Math.max(1, page);

  // Infinite/unknown total pages: show 1, (optional left ellipsis), a window around current, and a right ellipsis.
  if (typeof totalPages !== 'number') {
    const firstPage = 1;
    const nearStartMaxPage = safeSiblingCount + 2;
    const pages: PaginationItem[] = [];

    if (currentPage <= nearStartMaxPage) {
      const end = firstPage + windowSize;

      for (let p = firstPage; p <= end; p++) {
        pages.push(p);
      }

      return pages;
    }

    pages.push(firstPage);
    pages.push('ellipsis-left');

    const start = Math.max(firstPage + 1, currentPage - safeSiblingCount);
    const end = currentPage + safeSiblingCount;

    for (let p = start; p <= end; p++) {
      pages.push(p);
    }

    return pages;
  }

  if (totalPages <= 0) {
    return [];
  }

  if (totalPages === 1) {
    return [1];
  }

  // Small totals: just show everything
  if (totalPages <= windowSize + 2) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const firstPage = 1;
  const lastPage = totalPages;

  // Near-start and near-end windows try to show a full "block" of pages
  const nearStartMaxPage = safeSiblingCount + 2;
  const nearEndMinPage = totalPages - (safeSiblingCount + 1);

  const pages: PaginationItem[] = [];

  if (currentPage <= nearStartMaxPage) {
    const end = Math.min(lastPage - 1, firstPage + windowSize);
    for (let p = firstPage; p <= end; p++) {
      pages.push(p);
    }
    pages.push('ellipsis-right');
    pages.push(lastPage);
    return pages;
  }

  if (currentPage >= nearEndMinPage) {
    pages.push(firstPage);
    pages.push('ellipsis-left');

    const start = Math.max(firstPage + 1, lastPage - windowSize);
    for (let p = start; p <= lastPage; p++) {
      pages.push(p);
    }

    return pages;
  }

  // Middle
  pages.push(firstPage);
  pages.push('ellipsis-left');

  const start = Math.max(firstPage + 1, currentPage - safeSiblingCount);
  const end = Math.min(lastPage - 1, currentPage + safeSiblingCount);

  for (let p = start; p <= end; p++) {
    pages.push(p);
  }

  pages.push('ellipsis-right');
  pages.push(lastPage);

  return pages;
};

export type PaginationProps = {
  getPageHref?: (page: number) => string;
  hasNextPage?: boolean;
  onPageChange?: (page: number) => void;
  page: number; // 1-based
  siblingCount?: number; // defaults to 3 to match the example
  totalPages?: number;
};

export const Pagination = ({
  getPageHref,
  hasNextPage,
  onPageChange,
  page,
  siblingCount = 2,
  totalPages,
}: PaginationProps) => {
  const currentPage =
    typeof totalPages === 'number' ? clamp(page, 1, Math.max(1, totalPages)) : Math.max(1, page);

  const items = useMemo(
    () => getPaginationItems(currentPage, siblingCount, totalPages),
    [currentPage, siblingCount, totalPages],
  );

  if (typeof totalPages === 'number' && totalPages <= 1) {
    return null;
  }

  const canGoPrevious = currentPage > 1;
  const canGoNext =
    typeof totalPages === 'number' ? currentPage < totalPages : (hasNextPage ?? true);

  const navigateTo = (nextPage: number) => {
    if (nextPage < 1 || nextPage === currentPage) {
      return;
    }

    if (typeof totalPages === 'number' && nextPage > totalPages) {
      return;
    }

    onPageChange?.(nextPage);
  };

  const getHref = (targetPage: number) => getPageHref?.(targetPage);

  // For ellipsis items, find the nearest numeric page on either side and return a "jump" page in the middle.
  // This adds strong internal links for SEO while keeping visual the same.
  const getEllipsisJumpPage = (ellipsisIndex: number): null | number => {
    let previousNumericPage: null | number = null;
    let nextNumericPage: null | number = null;

    for (let i = ellipsisIndex - 1; i >= 0; i -= 1) {
      if (typeof items[i] === 'number') {
        previousNumericPage = items[i] as number;
        break;
      }
    }

    for (let i = ellipsisIndex + 1; i < items.length; i += 1) {
      if (typeof items[i] === 'number') {
        nextNumericPage = items[i] as number;
        break;
      }
    }

    if (
      previousNumericPage === null ||
      nextNumericPage === null ||
      nextNumericPage - previousNumericPage <= 1
    ) {
      return null;
    }

    const midpoint = Math.floor((previousNumericPage + nextNumericPage) / 2);

    if (midpoint === currentPage) {
      return null;
    }

    return midpoint;
  };

  const linkPropsFor = (targetPage: number, disabled?: boolean) => {
    const href = getHref(targetPage);

    if (!href) {
      return {};
    }

    return {
      _hover: {
        textDecoration: 'none',
      },
      as: Link,
      borderRadius: '12px',
      boxShadow: '0 4px 9px -2px rgba(95, 183, 202, 0.14), 0 2px 5px -2px rgba(95, 183, 202, 0.08)',
      href,
      pointerEvents: disabled ? 'none' : undefined,
      tabIndex: disabled ? -1 : undefined,
    } as const;
  };

  return (
    <Flex alignItems="center" gap={{ base: '8px', md: '16px' }} justifyContent="center" wrap="wrap">
      <Button
        borderColor={canGoPrevious ? 'teal.200 !important' : 'gray.200 !important'}
        fontSize="14px"
        isDisabled={!canGoPrevious}
        onClick={() => navigateTo(currentPage - 1)}
        size="md"
        variant="outline"
        {...linkPropsFor(currentPage - 1, !canGoPrevious)}
      >
        <Icon as={FaChevronLeft} fontSize="12px" mr="6px" />
        Previous
      </Button>

      <Flex gap={{ base: '8px', md: '16px' }} wrap="wrap">
        {items.map((item, index) => {
          if (item === 'ellipsis-left' || item === 'ellipsis-right') {
            const jumpPage = getEllipsisJumpPage(index);

            if (jumpPage) {
              return (
                <Button
                  aria-label={`Go to page ${jumpPage}`}
                  {...linkPropsFor(jumpPage)}
                  border="none"
                  boxShadow="none"
                  color="teal.400"
                  fontSize="md"
                  key={`${item}-${jumpPage}`}
                  onClick={() => navigateTo(jumpPage)}
                  size="md"
                  variant="ghost"
                >
                  ...
                </Button>
              );
            }

            return (
              <Box
                alignItems="center"
                color="gray.600"
                display="flex"
                fontSize="md"
                key={item}
                paddingX="8px"
              >
                ...
              </Box>
            );
          }

          const isCurrent = item === currentPage;

          return (
            <Button
              aria-current={isCurrent ? 'page' : undefined}
              backgroundColor={isCurrent ? 'teal.400 !important' : 'white !important'}
              border={isCurrent ? 'none' : '1px solid'}
              borderColor="teal.200"
              color={isCurrent ? 'white' : 'teal.400'}
              fontSize={isCurrent ? 'lg' : 'md'}
              fontWeight={500}
              key={`page-${item}`}
              onClick={() => navigateTo(item)}
              size="md"
              variant={isCurrent ? 'solid' : 'ghost'}
              {...linkPropsFor(item, isCurrent)}
            >
              {item}
            </Button>
          );
        })}
      </Flex>

      <Button
        borderColor={canGoNext ? 'teal.200 !important' : 'gray.200 !important'}
        fontSize="14px"
        isDisabled={!canGoNext}
        onClick={() => navigateTo(currentPage + 1)}
        size="md"
        variant="outline"
        {...linkPropsFor(currentPage + 1, !canGoNext)}
      >
        Next
        <Icon as={FaChevronRight} fontSize="12px" ml="6px" />
      </Button>
    </Flex>
  );
};
